import { useEffect, useMemo, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { usaStates } from 'typed-usa-states';
import { InputSearch, onChangeType } from 'components/Input';

import { FilterTags } from '../Search/FilterTags';
import { MultiSelectField } from '../Search/MultiSelectField';
import {
  categorizeTypeFilters,
  primaryColor,
  recipientCategoryOptions,
  secondaryColor,
} from '../Search/constants';
import {
  getAllowedSelectedTypes,
  getVisibleTypeOptions,
  resolveVisibleTypeFilters,
} from '../Search/typeFilters';
import Loader from 'components/Loader';
import { CandidateCard } from 'components/Cards/CandidateCard';
import { SelectedRecipientSidebarCard } from '../Search/SelectedRecipientSidebarCard';
import { BottomCart } from '../Search/BottomCart';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import {
  useFuzzySearchCartRecipientsQuery,
  useGetShoppingCartSearchConfigQuery,
} from 'services/shoppingCartApi';
import { useGetFiltersQuery } from 'services/filtersApi';
import { DEFAULT_CLIENT_ID, getPathWithRef, resolveClientId } from 'helpers/utils';
import { useAppDispatch } from 'helpers/hooks';
import { useAuth } from 'hooks/auth';
import { getContributionSummaries } from 'store/contributionSummary/slice';
import { getShoppingCartProps } from 'store/shopping_cart/types';
import { ErrorComponent } from 'components/Error';
import { trackDonorSearch, trackEvent } from 'helpers/mixpanelEvents';
import type { ShoppingCartSearchResultProps } from 'helpers/mixpanelEvents';
import classNames from 'classnames';
import ImpactScoreHelper from 'components/ListDescription/ImpactScoreHelper';
import { PopupModal } from 'components/PopupModal';
import { Link } from 'components/Link';
import {
  DonorSearchSource,
  RecipientsRequestType,
  isRestrictedStatus,
} from 'store/recipients/types';
import { NavigationType, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { useDelayedFlag } from 'hooks/useDelayedFlag';
import { useLeaveCartGuard } from 'hooks/useLeaveCartGuard';
import { useSavedCart } from 'hooks/useSavedCart';
import { LeaveCartModal } from './LeaveCartModal';

type FilterKey = 'slates' | 'states' | 'types';
const FILTER_KEYS: FilterKey[] = ['slates', 'states', 'types'];

// Only surface the loader for fetches slower than this, so fast requests keep the previous
// results visible instead of flashing a spinner.
const LOADER_DELAY_MS = 250;

const hasFilterParams = (slates: string[], states: string[], types: string[]) =>
  slates.length > 0 || states.length > 0 || types.length > 0;

export const SearchScreen = ({
  clientIdParam,
  alwaysShowImpactScore,
}: {
  clientIdParam?: number;
  alwaysShowImpactScore?: boolean;
}) => {
  // Use URL as the single source of truth for the search query and filters, enabling Back/Forward
  // navigation in browser history
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const dispatch = useAppDispatch();
  const auth0Requested = useAuth();

  const committedQuery = searchParams.get('q')?.replace('/', '') || '';
  const slates = useMemo(() => searchParams.getAll('slates'), [searchParams]);
  const states = useMemo(() => searchParams.getAll('states'), [searchParams]);
  const paramClientId = searchParams.get('client_id')?.replace('/', '') || undefined;
  const clientId = resolveClientId(clientIdParam, paramClientId);
  const requestLocationPath = location.pathname;

  const {
    data: shoppingCartSearchConfigData,
    currentData: shoppingCartSearchConfigCurrentData,
    isError: isShoppingCartSearchConfigError,
    error: shoppingCartSearchConfigError,
  } = useGetShoppingCartSearchConfigQuery({ client_id: clientId });
  const queryString = shoppingCartSearchConfigData?.query_string || '';
  // Searching before the Type allowlist is known could send a type this cart hides.
  const isShoppingCartSearchConfigPending =
    !shoppingCartSearchConfigCurrentData && !isShoppingCartSearchConfigError;

  useEffect(() => {
    if (!isShoppingCartSearchConfigError) return;
    const status =
      shoppingCartSearchConfigError && 'status' in shoppingCartSearchConfigError
        ? shoppingCartSearchConfigError.status
        : undefined;
    datadogLogs.logger.error(
      'Search page: shopping cart search config failed to load, showing every Type filter option',
      { client_id: clientId, http_status: status }
    );
  }, [isShoppingCartSearchConfigError, shoppingCartSearchConfigError, clientId]);

  // Options and tags for types this cart hides would flash while the config loads.
  const visibleTypeOptions = useMemo(() => {
    if (isShoppingCartSearchConfigPending) return [];
    return getVisibleTypeOptions(
      recipientCategoryOptions,
      resolveVisibleTypeFilters(clientId, shoppingCartSearchConfigCurrentData?.visible_type_filters)
    );
  }, [clientId, shoppingCartSearchConfigCurrentData, isShoppingCartSearchConfigPending]);
  const types = useMemo(
    () => getAllowedSelectedTypes(searchParams.getAll('types'), visibleTypeOptions),
    [searchParams, visibleTypeOptions]
  );

  const { data: filtersResponse } = useGetFiltersQuery({ client_id: clientId });
  const filtersData = filtersResponse?.filters || [];
  const isInitialSearch = !committedQuery && !hasFilterParams(slates, states, types);

  // Keep the search box editable while keeping it in sync with the URL's committed query, so Back/Forward and filter updates are reflected but typing doesn't trigger a search until submit
  const [q, setQ] = useState(committedQuery);
  useEffect(() => {
    setQ(committedQuery);
  }, [committedQuery]);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<CandidateType[]>([]);
  const [popupType, setPopupType] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { isRestoring, isSavingActive, flushBeforeNavigation } = useSavedCart({
    clientId,
    donorCartParam: searchParams.get('donor_cart') || undefined,
    selectedRecipients,
    setSelectedRecipients,
  });
  const showRestoringLoader = useDelayedFlag(isRestoring, LOADER_DELAY_MS);
  // Donate awaits the last save, so a second click could start a second navigation.
  const isDonatingRef = useRef(false);
  // A saved cart survives leaving, so only warn while saving is off.
  const { blocker, historyAction } = useLeaveCartGuard(
    isSavingActive ? 0 : selectedRecipients.length
  );

  const shoppingCartQueryArgs = useMemo<getShoppingCartProps>(() => {
    const base = {
      client_id: clientId,
      q: committedQuery,
      request_type: RecipientsRequestType.donor_search,
      request_location_path: requestLocationPath,
      client_query_str: paramClientId,
    };
    return hasFilterParams(slates, states, types)
      ? { ...base, slates, states, ...categorizeTypeFilters(types) }
      : base;
  }, [committedQuery, slates, states, types, clientId, requestLocationPath, paramClientId]);

  const slateOptions = useMemo(
    () =>
      filtersData?.length > 0
        ? filtersData.map(filter => ({ label: filter.filter_name, value: filter.filter_id }))
        : [],
    [filtersData]
  );

  const {
    data: shoppingCartData,
    currentData: shoppingCartCurrentData,
    isFetching: isShoppingCartFetching,
    isError: isShoppingCartError,
    error: shoppingCartError,
  } = useFuzzySearchCartRecipientsQuery(
    { ...shoppingCartQueryArgs, includeDefaults: isInitialSearch },
    { skip: isShoppingCartSearchConfigPending }
  );
  const showLoader = useDelayedFlag(
    isShoppingCartFetching || isShoppingCartSearchConfigPending,
    LOADER_DELAY_MS
  );

  // Fetch the donor's cycle giving for the recipients in the search results so
  // CandidateCard can render the contribution banner. Gated on auth0Requested to
  // limit to logged-in users.
  useEffect(() => {
    if (!auth0Requested) return;
    const recipientIds = shoppingCartData?.recipients?.map(r => r.recipient_id) ?? [];
    if (recipientIds.length) {
      dispatch(getContributionSummaries(recipientIds));
    }
  }, [shoppingCartData, auth0Requested, dispatch]);

  const lastTrackedSearchArgsRef = useRef<string | null>(null);
  const hasTrackedInitialLoadRef = useRef(false);
  const pendingDonorSearchRef = useRef<{ query: string; searchSource: DonorSearchSource } | null>(
    null
  );

  useEffect(() => {
    // Read currentData (results for the current args), not data, which RTK keeps pointing at the
    // previous results during a refetch — tracking against it would report stale counts.
    if (!shoppingCartCurrentData || isShoppingCartError) return;
    const recipients = shoppingCartCurrentData.recipients ?? [];

    // Fire the donor-search event once the submitted query's results arrive, so result_count
    // reflects current search result state
    const pendingDonorSearch = pendingDonorSearchRef.current;
    if (pendingDonorSearch && pendingDonorSearch.query === (shoppingCartQueryArgs.q ?? '')) {
      trackDonorSearch({
        search_source: pendingDonorSearch.searchSource,
        query: pendingDonorSearch.query,
        client_id: shoppingCartQueryArgs.client_id,
        result_count: recipients.length,
      });
      pendingDonorSearchRef.current = null;
    }

    // Decide whether (and what) to track before building the payload, so skipped
    // renders don't pay for mapping the full results list.
    let isRevisit = false;
    if (isInitialSearch) {
      if (hasTrackedInitialLoadRef.current) return;
      hasTrackedInitialLoadRef.current = true;
    } else {
      const argsKey = JSON.stringify(shoppingCartQueryArgs);
      if (lastTrackedSearchArgsRef.current === argsKey) return;
      // POP === Back/Forward. Require a prior tracked search so a first-load deep
      // link (also POP) is treated as a normal load, not a revisit.
      isRevisit =
        navigationType === NavigationType.Pop && lastTrackedSearchArgsRef.current !== null;
      lastTrackedSearchArgsRef.current = argsKey;
    }

    // Request values sent to the search API.
    const basePayload = {
      timestamp: new Date().toISOString(),
      ...shoppingCartQueryArgs,
    };

    // Response values layered on top of (and overriding) the request values.
    const responsePayload: ShoppingCartSearchResultProps = {
      ...basePayload,
      result_count: recipients.length,
      query_was_broadened: shoppingCartCurrentData.query_was_broadened,
      recipients: recipients.map(r => ({
        recipient_id: r.recipient_id,
        recipient_name: r.recipient_name,
        recipient_type: r.recipient_type,
        match_score: r.match_score,
      })),
    };

    if (isRevisit) {
      trackEvent('Shopping Cart Search Revisited', responsePayload);
      return;
    }

    trackEvent('Shopping Cart Search Results Loaded', {
      ...responsePayload,
      is_initial_load: isInitialSearch,
    });
  }, [
    shoppingCartCurrentData,
    shoppingCartQueryArgs,
    isInitialSearch,
    isShoppingCartError,
    navigationType,
  ]);

  // URL writers. Free-text search and filters are mutually exclusive, so each
  // writer clears the other side.
  const setFilter = (key: FilterKey, values: string[]) => {
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    next.delete(key);
    // Keeps the URL in sync with the API request when a restricted group loses its last child.
    const nextValues =
      key === 'types' ? getAllowedSelectedTypes(values, visibleTypeOptions) : values;
    nextValues.forEach(value => next.append(key, value));
    setSearchParams(next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    FILTER_KEYS.forEach(key => next.delete(key));
    setSearchParams(next);
  };

  const commitQuery = (value: string) => {
    const next = new URLSearchParams(searchParams);
    FILTER_KEYS.forEach(key => next.delete(key));
    if (value) {
      next.set('q', value);
    } else {
      next.delete('q');
    }
    setSearchParams(next);
  };

  const handleSearchSubmit = (key: string) => {
    if (!key) return;

    if (key === 'Enter') {
      pendingDonorSearchRef.current = {
        query: q,
        searchSource:
          clientId === DEFAULT_CLIENT_ID
            ? DonorSearchSource.oath_cart_search
            : DonorSearchSource.client_cart_search,
      };
      commitQuery(q);
      trackEvent('Shopping Cart Search Bar Submit', {
        timestamp: new Date().toISOString(),
        search_value: q,
        client_id: shoppingCartQueryArgs.client_id,
      });
    } else if (key === 'Backspace' && q.length === 1) {
      commitQuery('');
    }
  };

  const handleDonateClick = async () => {
    if (isDonatingRef.current) return;
    isDonatingRef.current = true;
    try {
      // Donate is a full page load; the last save has to start before it.
      let savedCartId: string | undefined;
      if (isSavingActive) {
        try {
          savedCartId = await flushBeforeNavigation();
        } catch {
          // Saving must never block Donate
          datadogLogs.logger.error(
            'Saved cart flush before Donate failed - navigating without donor_cart',
            {
              client_id: clientId,
            }
          );
        }
      }
      const shoppingCartParam = selectedRecipients.map(rec => rec.recipient_id).join(',');

      let donatePath = '/donate?';
      if (queryString && queryString.trim().length > 0) {
        donatePath += `p=${encodeURIComponent(queryString)}&`;
      }
      donatePath += `shopping-cart=${shoppingCartParam}`;
      if (savedCartId) {
        donatePath += `&donor_cart=${savedCartId}`;
      }

      const targetUrl = getPathWithRef(donatePath);

      if (window.top && window.top !== window.self) {
        const a = document.createElement('a');
        a.href = targetUrl;
        a.target = '_top';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        window.location.href = targetUrl;
      }
    } finally {
      // Reset, so Donate still works if the navigation is blocked or the page comes back from bfcache.
      isDonatingRef.current = false;
    }
  };

  const stateOptions = usaStates
    .filter(state => !state.territory)
    .map(state => ({
      label: state.name,
      value: state.name,
    }));

  const { availableRecipients, addableRecipients } = useMemo(() => {
    if (!shoppingCartData?.recipients) {
      return { availableRecipients: [], addableRecipients: [] };
    }

    const selectedIds = new Set(selectedRecipients.map(recipient => recipient.recipient_id));

    const available = shoppingCartData.recipients
      .filter(recipient => !selectedIds.has(recipient.recipient_id))
      .map((recipient, index) => ({ ...recipient, index, isSelected: false }));

    const addable = available.filter(r => !isRestrictedStatus(r.recipient_status));

    return { availableRecipients: available, addableRecipients: addable };
  }, [shoppingCartData, selectedRecipients]);

  const handleSelectFromAvailable = (index: number, check: boolean) => {
    if (!check || isRestoring) return;
    setSelectedRecipients(prev => [...prev, { ...availableRecipients[index], isSelected: true }]);
  };

  const handleSelectFromSelected = (index: number, check: boolean) => {
    if (check) return;
    setSelectedRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const allRestricted = availableRecipients.length > 0 && addableRecipients.length === 0;
  const hasMixedEligibility = addableRecipients.length < availableRecipients.length;
  const recipientsLabel = hasMixedEligibility ? 'eligible recipients' : 'recipients';

  const handleQuickAdd = () => {
    if (isRestoring) return;
    setSelectedRecipients(prev => [
      ...prev,
      ...addableRecipients.map(r => ({ ...r, isSelected: true })),
    ]);
  };

  // Rendered on the error path too: the guard still blocks there, and a hidden modal would trap the donor.
  const leaveCartModal = (
    <LeaveCartModal
      blocker={blocker}
      historyAction={historyAction}
      trackingProps={{
        client_id: clientId,
        page_query_string: queryString,
        recipient_count: selectedRecipients.length,
      }}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
    />
  );

  if (isShoppingCartError) {
    return (
      <div className="search-container">
        <div className="search-recipients-section">
          <ErrorComponent
            data={{
              component: 'SearchScreen',
              errorMessage: JSON.stringify(shoppingCartError),
            }}
          >
            <div className="error">
              <h1>Error</h1>
              <p>An error occurred while loading the shopping cart.</p>
            </div>
          </ErrorComponent>
        </div>
        {leaveCartModal}
      </div>
    );
  }
  return (
    <div className="search-container">
      <div className="search-left">
        <div className="search-filters-section sticky-header">
          <InputSearch
            id="searchValue"
            placeholder="Search by candidates, issues, or states..."
            onChange={(e: onChangeType) => setQ(e.target.value)}
            value={q}
            autoCapitalize="words"
            onSubmit={handleSearchSubmit}
          />
          {!showLoader && shoppingCartData?.query_was_broadened && (
            <div className="help-text">
              <span className="body-text color-oathBlue">
                We broadened your search to show more results.
              </span>
            </div>
          )}

          <div className="search-filters-form">
            <button
              type="button"
              className="mobile-filters-toggle eyebrow-text color-inkBlue"
              onClick={() => setMobileFiltersOpen(prev => !prev)}
              aria-expanded={mobileFiltersOpen}
            >
              {mobileFiltersOpen
                ? 'Hide Filters'
                : `See Filters (${slates.length + states.length + types.length})`}
            </button>

            <div
              className={classNames('filter-options-section mobile-collapsible', {
                'is-open': mobileFiltersOpen,
              })}
            >
              <span className="eyebrow-text color-inkBlue">Filter by</span>
              <div className="search-select-wrapper">
                <MultiSelectField
                  options={slateOptions}
                  label="Slates"
                  value={slates}
                  onChange={next => setFilter('slates', next)}
                />
              </div>
              <div className="search-select-wrapper">
                <MultiSelectField
                  options={stateOptions}
                  label="States"
                  value={states}
                  onChange={next => setFilter('states', next)}
                />
              </div>
              <div className="search-select-wrapper">
                <MultiSelectField
                  options={visibleTypeOptions}
                  label="Type"
                  value={types}
                  onChange={next => setFilter('types', next)}
                />
              </div>
            </div>
            <FilterTags
              slates={slates}
              states={states}
              types={types}
              slateOptions={slateOptions}
              stateOptions={stateOptions}
              typeOptions={visibleTypeOptions}
              onChange={setFilter}
              onClearAll={clearFilters}
            />
          </div>
          {!showLoader &&
            (availableRecipients.length > 0 ? (
              <div className="row justify-between help-text found-search-results">
                {allRestricted ? (
                  <span className="body-text inkBlue">
                    All recipients are ineligible to receive donations.
                  </span>
                ) : (
                  <span className="body-text inkBlue">
                    Click the + sign to add select recipients to your cart or
                    <button type="button" className="link-help-text" disabled={isRestoring}>
                      <a
                        href="#"
                        className="color-oathBlue body-text"
                        aria-disabled={isRestoring}
                        onClick={e => {
                          e.preventDefault();
                          handleQuickAdd();
                        }}
                      >
                        Add all {addableRecipients.length} {recipientsLabel} to cart
                      </a>
                    </button>
                  </span>
                )}
              </div>
            ) : shoppingCartData?.recipients && shoppingCartData.recipients.length > 0 ? (
              <div className="row justify-between help-text">
                <span className="body-small-text inkBlue">
                  All matching search results are in your cart 🎉
                </span>
              </div>
            ) : (
              <div className="row justify-between help-text">
                <span className="body-small-text inkBlue">
                  Your search returned no additional results. Oath is only recommending select
                  candidates running in {new Date().getFullYear()} elections who are not in
                  competitive primaries. To submit a candidate for consideration, visit our{' '}
                  <a
                    className="color-oathBlue"
                    href="https://helpcenter.oath.vote/oath-help-center/i-know-a-candidate-or-organization-that-could-be-a-great-fit-for-the-oath-platform.-how-do-i-recommend-them"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    candidate suggestion form
                  </a>
                  .
                </span>
              </div>
            ))}
        </div>
        <div className="search-recipients-section">
          <ImpactScoreHelper setPopupType={setPopupType} setIsPopupOpen={setIsPopupOpen} />
          <div className="line" />
          {showLoader ? (
            <div className="loader-center">
              <Loader width={60} height={60} />
            </div>
          ) : availableRecipients.length > 0 ? (
            <>
              {availableRecipients?.map((item, i) => (
                <CandidateCard
                  key={item.recipient_id}
                  index={i}
                  item={item}
                  variant="search"
                  selectItem={handleSelectFromAvailable}
                  disabled={isRestoring}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  impactScoreVisibility={
                    alwaysShowImpactScore || shoppingCartSearchConfigData?.impact_score_visibility
                  }
                  showMoreVisibility={shoppingCartSearchConfigData?.show_more_visibility}
                />
              ))}
            </>
          ) : null}
          {!showLoader && addableRecipients.length > 0 && (
            <div className="quick-add-all-button">
              <button
                type="button"
                className="button button-brand full-width quick-add-all-button"
                style={{ backgroundColor: primaryColor, color: secondaryColor }}
                onClick={handleQuickAdd}
                disabled={isRestoring}
              >
                Add All to Cart
                <div className="body-small-text color-white lowercase">
                  <span>
                    {addableRecipients.length} {recipientsLabel} selected
                  </span>
                </div>
              </button>
            </div>
          )}
        </div>
        <BottomCart
          items={selectedRecipients || []}
          onRemove={(index: number) => handleSelectFromSelected(index, false)}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          onDonate={handleDonateClick}
          isLoading={showRestoringLoader}
        />
        {selectedRecipients && selectedRecipients.length > 0 && (
          <div className="sidebar-donate-sticky">
            <button
              className="button button-brand full-width donate-now"
              style={{ backgroundColor: primaryColor, color: secondaryColor }}
              onClick={handleDonateClick}
            >
              DONATE NOW
            </button>
          </div>
        )}
      </div>
      <div className="search-cart-section">
        <SelectedRecipientSidebarCard
          recipients={selectedRecipients || []}
          onRemove={(index: number) => handleSelectFromSelected(index, false)}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          donatePosition="after-list"
          onDonate={handleDonateClick}
          isLoading={showRestoringLoader}
        />
        {popupType === 'IMPACT' && (
          <PopupModal
            autoClose={false}
            closePopup={() => setIsPopupOpen(false)}
            isSelected={isPopupOpen}
            title="What's the Impact Score?"
            description={
              <>
                Oath's Impact Score is calculated from dozens of data points across three metrics:
                Competitiveness, Stakes, and Financial Need.{' '}
                <Link to={getPathWithRef('/impact_score')} target="_blank">
                  Learn more.
                </Link>
              </>
            }
          />
        )}
      </div>
      {leaveCartModal}
    </div>
  );
};
